import { FC, HTMLAttributes, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { ObservationQuestionLayout } from "../ObservationQuestion/ObservationQuestion";

interface ObservationFormPreviewProps extends HTMLAttributes<HTMLDivElement> {
	questions: ObservationQuestion[];
	formName?: string;
}

const ObservationFormPreview: FC<ObservationFormPreviewProps> = ({
	questions,
	formName,
	...restProps
}) => {
	const methods = useForm<ObservationFormState>({
		defaultValues: { questions }
	});

	useEffect(() => {
		methods.setValue("questions", questions);
	}, [questions]);

	const renderQuestion = (question: ObservationQuestion) => {
		const { questionType, givenAnswer, images } = question;

		// Don't show image upload questions with no images
		if (questionType === "IMAGE_UPLOAD" && images.length === 0) return null;

		// If standard question is not answered, don't show it
		// standard is anything thats not heading or image upload
		if (
			questionType !== "HEADING" &&
			questionType !== "IMAGE_UPLOAD" &&
			!givenAnswer
		)
			return null;

		return (
			<ObservationQuestionLayout
				key={question.id}
				{...question}
				isDisabled={false}
				isLoading={false}
				questionNumber={question.order}
				error={false}
				showErrorBorder={
					(question.isCorrectAnswerRequired &&
						question.isGreatConversationCorrect === false) ||
					question.compliant === -1
				}
				formStatic={true}
				showImageUploadButton={false}
				showAction={Boolean(question.isActionRequired)}
				image={question.imageCoverId ?? undefined}
			/>
		);
	};

	const sortedQuestions = questions.sort(
		(question1, question2) => question1.order - question2.order
	);

	return (
		<div data-testid="component.ObservationFormPreview" {...restProps}>
			<FormProvider {...methods}>
				{formName && (
					<h1 className="font-heading text-xl font-semibold text-foreground mb-6">
						{formName}
					</h1>
				)}

				<div className="flex flex-col gap-4 bg-canvas rounded-lg p-4">
					{sortedQuestions.map(renderQuestion)}
				</div>
			</FormProvider>
		</div>
	);
};

export default ObservationFormPreview;
